/**
 * Cross-Tenant Access API Service
 *
 * Provides methods for managing Microsoft Entra cross-tenant access settings
 * including default policies and partner configurations.
 *
 * Uses Bearer token authentication via MSAL.
 */

import { ApiService } from './api';
import type {
  CrossTenantDefaultPolicy,
  CrossTenantDefaultPolicyUpdateRequest,
  CrossTenantPartner,
  CrossTenantPartnerCreateRequest,
  CrossTenantPartnerUpdateRequest,
} from '../types/crossTenantAccess';

/**
 * Cross-Tenant Access API Client
 */
class CrossTenantAccessApiServiceClass {
  /**
   * Get base URL for cross-tenant access endpoints.
   */
  private getBaseUrl(tenantId: string): string {
    return `/api/tenants/${tenantId}/security/policies/cross-tenant-access`;
  }

  /**
   * Get the default cross-tenant access policy.
   *
   * @param tenantId - Tenant ID
   * @returns Default policy with inbound trust settings
   */
  async getDefaultPolicy(tenantId: string): Promise<CrossTenantDefaultPolicy> {
    return ApiService.request<CrossTenantDefaultPolicy>(
      `${this.getBaseUrl(tenantId)}/default`,
      { method: 'GET' }
    );
  }

  /**
   * Update the default cross-tenant access policy.
   *
   * @param tenantId - Tenant ID
   * @param update - Policy settings to update
   */
  async updateDefaultPolicy(
    tenantId: string,
    update: CrossTenantDefaultPolicyUpdateRequest
  ): Promise<void> {
    await ApiService.request(
      `${this.getBaseUrl(tenantId)}/default`,
      {
        method: 'PATCH',
        body: JSON.stringify(update),
      }
    );
  }

  /**
   * List all partner configurations.
   * Returns partners with displayName and defaultDomainName.
   * If tenant info lookup fails, the request fails - no fallbacks.
   *
   * @param tenantId - Tenant ID
   * @returns List of partner configurations
   */
  async listPartners(tenantId: string): Promise<CrossTenantPartner[]> {
    return ApiService.request<CrossTenantPartner[]>(
      `${this.getBaseUrl(tenantId)}/partners`,
      { method: 'GET' }
    );
  }

  /**
   * Create a new partner configuration.
   *
   * @param tenantId - Tenant ID
   * @param partner - Partner configuration to create
   * @returns Created partner configuration
   */
  async createPartner(
    tenantId: string,
    partner: CrossTenantPartnerCreateRequest
  ): Promise<CrossTenantPartner> {
    return ApiService.request<CrossTenantPartner>(
      `${this.getBaseUrl(tenantId)}/partners`,
      {
        method: 'POST',
        body: JSON.stringify(partner),
      }
    );
  }

  /**
   * Get a specific partner configuration.
   *
   * @param tenantId - Tenant ID
   * @param partnerTenantId - Partner tenant ID
   * @returns Partner configuration
   */
  async getPartner(
    tenantId: string,
    partnerTenantId: string
  ): Promise<CrossTenantPartner> {
    return ApiService.request<CrossTenantPartner>(
      `${this.getBaseUrl(tenantId)}/partners/${partnerTenantId}`,
      { method: 'GET' }
    );
  }

  /**
   * Update a partner configuration.
   *
   * @param tenantId - Tenant ID
   * @param partnerTenantId - Partner tenant ID
   * @param update - Settings to update
   */
  async updatePartner(
    tenantId: string,
    partnerTenantId: string,
    update: CrossTenantPartnerUpdateRequest
  ): Promise<void> {
    await ApiService.request(
      `${this.getBaseUrl(tenantId)}/partners/${partnerTenantId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(update),
      }
    );
  }

  /**
   * Delete a partner configuration.
   *
   * @param tenantId - Tenant ID
   * @param partnerTenantId - Partner tenant ID
   */
  async deletePartner(tenantId: string, partnerTenantId: string): Promise<void> {
    await ApiService.request(
      `${this.getBaseUrl(tenantId)}/partners/${partnerTenantId}`,
      { method: 'DELETE' }
    );
  }
}

export const CrossTenantAccessApiService = new CrossTenantAccessApiServiceClass();
